import { CardProps, LinearProgress, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { ReactNode } from 'react';
import { resolveKpiProgressGradientColor } from './kpi-progress-gradient';
import { KpiBaseCard, KpiCardSize } from './KpiBaseCard';
import { KpiValue } from './KpiValue';

export type KpiCardProgressColor =
  | 'primary'
  | 'secondary'
  | 'error'
  | 'info'
  | 'success'
  | 'warning'
  | 'inherit';

export interface KpiCardProgressProps extends Omit<CardProps, 'title' | 'color'> {
  /** Main KPI label displayed on top of the card. */
  title: ReactNode;
  /**
   * Current KPI value shown as the primary metric.
   * When omitted, a percentage derived from `progress` is rendered so callers
   * do not have to pass the same figure twice.
   */
  value?: ReactNode;
  /** Completion percentage between 0 and 100. */
  progress: number;
  /**
   * Optional caption rendered at the right of the value row.
   * Opt-in so percentage KPIs do not duplicate the hero value; useful when the hero value
   * is in a different unit (e.g. duration) and the percentage is extra context.
   */
  progressLabel?: ReactNode;
  /** Optional helper text (example: "Objetivo mensual"). */
  subtitle?: ReactNode;
  /** Optional help content shown in a tooltip (question icon at the top right). */
  help?: ReactNode;
  /** Optional content aligned to the right of the value row (same slot as `KpiCardTrend`). */
  valueRight?: ReactNode;
  /**
   * Optional fixed MUI `LinearProgress` color.
   * When omitted, the bar (and the derived percentage value) use a continuous
   * error → warning → success gradient from `progress`.
   */
  color?: KpiCardProgressColor;
  /** Layout density; defaults to `md`. */
  size?: KpiCardSize;
}

/**
 * KPI card variant for progress/goal indicators.
 * The bar is a full-bleed strip along the card bottom edge, which keeps the content box
 * identical to the other KPI variants so values stay aligned in mixed grid rows.
 */
export function KpiCardProgress({
  title,
  value,
  progress,
  progressLabel,
  subtitle,
  help,
  valueRight,
  color,
  size = 'md',
  sx,
  ...cardProps
}: KpiCardProgressProps) {
  const theme = useTheme();
  const clampedProgress = Math.max(0, Math.min(progress, 100));
  const isSm = size === 'sm';
  const barHeight = isSm ? 5 : 6;
  const useGradient = color == null;
  const gradientColor = useGradient
    ? resolveKpiProgressGradientColor(clampedProgress / 100, theme.palette)
    : undefined;

  const resolvedValue = value ?? (
    <KpiValue
      type="percentage"
      value={clampedProgress}
      maximumFractionDigits={0}
      color={gradientColor}
    />
  );

  const progressLabelNode =
    progressLabel != null ? (
      <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
        {progressLabel}
      </Typography>
    ) : null;

  /** Both slots can be used at once, so they share the value row when present. */
  const resolvedValueRight =
    valueRight != null && progressLabelNode != null ? (
      <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
        {valueRight}
        {progressLabelNode}
      </Stack>
    ) : (
      (valueRight ?? progressLabelNode)
    );

  return (
    <KpiBaseCard
      title={title}
      value={resolvedValue}
      valueRight={resolvedValueRight}
      help={help}
      size={size}
      footer={subtitle ?? null}
      bottomStrip={
        <LinearProgress
          variant="determinate"
          value={clampedProgress}
          color={useGradient ? 'inherit' : color}
          sx={{
            width: '100%',
            height: barHeight,
            backgroundColor: 'divider',
            ...(useGradient
              ? {
                  color: gradientColor,
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: gradientColor,
                  },
                }
              : null),
          }}
        />
      }
      sx={sx}
      {...cardProps}
    />
  );
}
